import type { ReactNode } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { DndProvider, usePreview } from 'react-dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { TILE_VALUES } from '@scrabble-daily/engine';
import type { TileDto } from '../../api/puzzles';

export const TILE_DND_TYPE = 'TILE';

export interface CellPos {
  row: number;
  col: number;
}

export interface DragTileItem {
  tile: TileDto;
  /** Set when dragging a tile already staged on the board — drives move/swap/recall. */
  from?: CellPos;
}

/** HTML5 backend on desktop, touch backend on mobile — both work (spec 10.4). */
export function GameDndProvider({ children }: { children: ReactNode }) {
  return (
    <DndProvider options={HTML5toTouch}>
      {children}
      {usesTouchPreview() ? <TileDragPreview /> : null}
    </DndProvider>
  );
}

/**
 * Only mount the finger-following preview on touch devices (`pointer: coarse`).
 * It's redundant on desktop (the HTML5 backend draws its own drag image), and
 * mounting it pulls in usePreview's drag-layer subscription, which destabilises
 * async unit tests in jsdom (no matchMedia there → this returns false).
 */
function usesTouchPreview(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

/**
 * A floating tile that tracks the finger while dragging. The HTML5 backend
 * (desktop) draws its own native drag image, so usePreview only renders for
 * the touch backend (mobile) — exactly where there was otherwise no feedback.
 */
function TileDragPreview() {
  const preview = usePreview<DragTileItem>();
  if (!preview.display) return null;
  const { letter, is_blank } = preview.item.tile;
  const value = is_blank ? 0 : (TILE_VALUES[letter] ?? 0);
  return (
    <div style={preview.style} className="pointer-events-none z-50">
      <div className="flex h-11 w-11 scale-105 items-center justify-center rounded-md border-b-4 border-tile-edge bg-tile-face text-xl font-bold text-stone-900 shadow-xl ring-2 ring-amber-400">
        {letter}
        {letter ? <sub className="ml-0.5 align-sub text-[9px] font-medium">{value}</sub> : null}
      </div>
    </div>
  );
}

export function DraggableTile({
  tile,
  from,
  children,
}: {
  tile: TileDto;
  /** Pass when the tile is already on the board, so it can be moved/recalled. */
  from?: CellPos;
  children: ReactNode;
}) {
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: TILE_DND_TYPE,
      item: { tile, from } satisfies DragTileItem,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [tile, from?.row, from?.col],
  );
  return (
    <div ref={dragRef} className={isDragging ? 'opacity-40' : undefined}>
      {children}
    </div>
  );
}

/** Rack slot drop target: dropping a rack tile here re-arranges the hand. */
export function RackDropSlot({
  index,
  onDropTile,
  children,
}: {
  index: number;
  onDropTile: (tile: TileDto, index: number, from?: CellPos) => void;
  children: ReactNode;
}) {
  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: TILE_DND_TYPE,
      drop: (item: DragTileItem) => onDropTile(item.tile, index, item.from),
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [index, onDropTile],
  );
  return (
    <div ref={dropRef} className={isOver ? 'rounded-md ring-2 ring-emerald-500' : undefined}>
      {children}
    </div>
  );
}

export function DroppableCell({
  row,
  col,
  onDropTile,
  children,
}: {
  row: number;
  col: number;
  onDropTile: (row: number, col: number, tile: TileDto, from?: CellPos) => void;
  children: ReactNode;
}) {
  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: TILE_DND_TYPE,
      drop: (item: DragTileItem) => onDropTile(row, col, item.tile, item.from),
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [row, col, onDropTile],
  );
  return (
    <div ref={dropRef} className={`aspect-square ${isOver ? 'ring-2 ring-emerald-500' : ''}`}>
      {children}
    </div>
  );
}
